import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { productService } from '../services/productService';
import Button from '../components/Button/Button';
import LoadingSpinner from '../components/LoadingSpinner/LoadingSpinner';

const HomeWrapper = styled.div`
  min-height: 100vh;
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}15 0%, ${props => props.theme.colors.primaryLight} 100%);
  padding: ${props => props.theme.spacing.xxxl} 0;
  text-align: center;
`;

const HeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.lg};
`;

const HeroTitle = styled.h1`
  font-size: ${props => props.theme.fontSize.xxxl};
  font-weight: ${props => props.theme.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
  margin-bottom: ${props => props.theme.spacing.lg};
  line-height: 1.2;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    font-size: ${props => props.theme.fontSize.xxl};
  }
`;

const HeroSubtitle = styled.p`
  font-size: ${props => props.theme.fontSize.lg};
  color: ${props => props.theme.colors.text.secondary};
  margin-bottom: ${props => props.theme.spacing.xxl};
  line-height: 1.5;
`;

const HeroActions = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.lg};
  justify-content: center;
  flex-wrap: wrap;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    flex-direction: column;
    align-items: center;
  }
`;

const FeaturedSection = styled.section`
  padding: ${props => props.theme.spacing.xxxl} 0;
  background-color: ${props => props.theme.colors.background.primary};
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.lg};
`;

const SectionTitle = styled.h2`
  font-size: ${props => props.theme.fontSize.xxl};
  font-weight: ${props => props.theme.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
  text-align: center;
  margin-bottom: ${props => props.theme.spacing.xxl};
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${props => props.theme.spacing.xl};
  margin-bottom: ${props => props.theme.spacing.xxl};
`;

const ProductCard = styled.div`
  background-color: ${props => props.theme.colors.background.primary};
  border: 1px solid ${props => props.theme.colors.border.light};
  border-radius: ${props => props.theme.borderRadius.lg};
  overflow: hidden;
  transition: all ${props => props.theme.transitions.normal};

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${props => props.theme.shadows.lg};
  }
`;

const ProductImage = styled.div`
  width: 100%;
  height: 200px;
  background-color: ${props => props.theme.colors.background.secondary};
  background-image: url(${props => props.image});
  background-size: cover;
  background-position: center;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${props => !props.image ? 'linear-gradient(45deg, #f0f0f0 25%, transparent 25%), linear-gradient(-45deg, #f0f0f0 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #f0f0f0 75%), linear-gradient(-45deg, transparent 75%, #f0f0f0 75%)' : 'none'};
    background-size: ${props => !props.image ? '20px 20px' : 'auto'};
    background-position: ${props => !props.image ? '0 0, 0 10px, 10px -10px, -10px 0px' : 'auto'};
  }
`;

const ProductContent = styled.div`
  padding: ${props => props.theme.spacing.lg};
`;

const ProductTitle = styled.h3`
  font-size: ${props => props.theme.fontSize.base};
  font-weight: ${props => props.theme.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
  margin-bottom: ${props => props.theme.spacing.sm};
  line-height: 1.3;
`;

const ProductPrice = styled.p`
  font-size: ${props => props.theme.fontSize.lg};
  font-weight: ${props => props.theme.fontWeight.bold};
  color: ${props => props.theme.colors.primary};
  margin-bottom: ${props => props.theme.spacing.md};
`;

const ProductDescription = styled.p`
  font-size: ${props => props.theme.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
  margin-bottom: ${props => props.theme.spacing.lg};
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ViewAllButton = styled.div`
  text-align: center;
`;

const HomePage = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchFeaturedProducts = async () => {
      try {
        const products = await productService.getFeaturedProducts(8);
        setFeaturedProducts(products);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedProducts();
  }, []);

  if (loading) {
    return <LoadingSpinner fullHeight />;
  }

  return (
    <HomeWrapper>
      <HeroSection>
        <HeroContent>
          <HeroTitle>Premium Digital Products</HeroTitle>
          <HeroSubtitle>
            Discover high-quality digital downloads, templates, and tools 
            to enhance your projects and boost your productivity.
          </HeroSubtitle>
          <HeroActions>
            <Button as={Link} to="/shop" size="lg">
              Browse Products
            </Button>
            <Button as={Link} to="/register" variant="outline" size="lg">
              Get Started
            </Button>
          </HeroActions>
        </HeroContent>
      </HeroSection>

      <FeaturedSection>
        <Container>
          <SectionTitle>Featured Products</SectionTitle>
          
          {error ? (
            <div className="error">Failed to load featured products: {error}</div>
          ) : (
            <>
              <ProductGrid>
                {featuredProducts.map((product) => (
                  <ProductCard key={product.id}>
                    <ProductImage 
                      image={productService.getProductImage(product)}
                    />
                    <ProductContent>
                      <ProductTitle>{product.name}</ProductTitle>
                      <ProductPrice>
                        {productService.formatPrice(product.price)}
                      </ProductPrice>
                      <ProductDescription 
                        dangerouslySetInnerHTML={{ 
                          __html: product.short_description || product.description 
                        }} 
                      />
                      <Button 
                        as={Link} 
                        to={`/product/${product.id}`}
                        fullWidth
                        size="sm"
                      >
                        View Details
                      </Button>
                    </ProductContent>
                  </ProductCard>
                ))}
              </ProductGrid>

              <ViewAllButton>
                <Button as={Link} to="/shop" variant="outline" size="lg">
                  View All Products
                </Button>
              </ViewAllButton>
            </>
          )}
        </Container>
      </FeaturedSection>
    </HomeWrapper>
  );
};

export default HomePage;